/**
 * Abandoned calls table widget — table of records: date/time, campaign, caller number, time in queue.
 * Uses same API and config (campaign_id, date_from, date_to) as WidgetAbandonedInQueue.
 */
import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { buildCallHistoryParams, type WidgetFilterConfig } from '../../lib/widgetScope';

export interface AbandonedCallRow {
  id: string;
  activity_id: string;
  campaign_id: number;
  campaign_name?: string;
  time_in_workflow?: number;
  time_in_queue?: number;
  total_wait_time?: number;
  number_called?: string;
  callers_number?: string;
  timestamp: string;
}

interface WidgetAbandonedCallsTableProps {
  config?: WidgetFilterConfig;
}

const PAGE_SIZE_OPTIONS = [25, 50] as const;

function formatTimeInQueue(sec?: number): string {
  if (sec == null) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function WidgetAbandonedCallsTable({ config }: WidgetAbandonedCallsTableProps) {
  const { getToken } = useAuth();
  const [items, setItems] = useState<AbandonedCallRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await getToken();
      if (!token || cancelled) return;
      setLoading(true);
      const base = typeof window !== 'undefined' ? window.location.origin : '';
      const params = buildCallHistoryParams(config);
      params.set('limit', String(pageSize));
      params.set('offset', String(page * pageSize));
      try {
        const res = await fetch(`${base}/api/abandoned-calls?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (res.status === 403) {
          setError('Supervisor or admin access required');
          setItems([]);
          setTotal(0);
          return;
        }
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setError(data.error || `HTTP ${res.status}`);
          setItems([]);
          setTotal(0);
          return;
        }
        const data = await res.json();
        setItems(data.items ?? []);
        setTotal(data.total ?? 0);
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getToken, config?.scope, config?.campaign_id, config?.ipscape_agent_id, config?.date_range, page, pageSize]);

  useEffect(() => {
    setPage(0);
  }, [config?.scope, config?.campaign_id, config?.ipscape_agent_id, config?.date_range, pageSize]);

  const formatTs = (ts: string) => {
    try {
      return new Date(ts).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'medium' });
    } catch {
      return ts;
    }
  };

  const campaignLabel = (row: AbandonedCallRow) => row.campaign_name?.trim() || String(row.campaign_id);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = page + 1;

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        padding: '0.5rem 0.75rem',
      }}
    >
      {loading && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
          Loading…
        </div>
      )}
      {error && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--error)', fontSize: '0.875rem', textAlign: 'center' }}>
          {error}
        </div>
      )}
      {!loading && !error && (
        <>
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            {items.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>No abandoned calls in range.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'clamp(0.65rem, 1.8cqi, 0.85rem)' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--header-border)', textAlign: 'left' }}>
                    <th style={{ padding: '0.35rem 0.5rem', fontWeight: 600 }}>Date / Time</th>
                    <th style={{ padding: '0.35rem 0.5rem', fontWeight: 600 }}>Campaign</th>
                    <th style={{ padding: '0.35rem 0.5rem', fontWeight: 600 }}>Caller</th>
                    <th style={{ padding: '0.35rem 0.5rem', fontWeight: 600 }}>Time in queue</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.id} style={{ borderBottom: '1px solid var(--header-border)' }}>
                      <td style={{ padding: '0.35rem 0.5rem', whiteSpace: 'nowrap' }}>{formatTs(row.timestamp)}</td>
                      <td style={{ padding: '0.35rem 0.5rem' }}>{campaignLabel(row)}</td>
                      <td style={{ padding: '0.35rem 0.5rem' }}>{row.callers_number || row.number_called || '—'}</td>
                      <td style={{ padding: '0.35rem 0.5rem' }}>{formatTimeInQueue(row.time_in_queue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {total > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '0.5rem',
                paddingTop: '0.5rem',
                borderTop: '1px solid var(--header-border)',
                flexShrink: 0,
              }}
            >
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                {total} call{total !== 1 ? 's' : ''}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  style={{
                    padding: '0.25rem 0.5rem',
                    fontSize: '0.75rem',
                    borderRadius: 4,
                    border: '1px solid var(--header-border)',
                    background: 'var(--bg)',
                    color: 'var(--text)',
                  }}
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n} per page</option>
                  ))}
                </select>
                {totalPages > 1 && (
                  <>
                    <button
                      type="button"
                      disabled={page === 0}
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      style={{
                        padding: '0.25rem 0.5rem',
                        fontSize: '0.75rem',
                        borderRadius: 4,
                        border: '1px solid var(--header-border)',
                        background: 'var(--widget-bg)',
                        color: 'var(--text)',
                        cursor: page === 0 ? 'not-allowed' : 'pointer',
                        opacity: page === 0 ? 0.6 : 1,
                      }}
                    >
                      Prev
                    </button>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      type="button"
                      disabled={page >= totalPages - 1}
                      onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                      style={{
                        padding: '0.25rem 0.5rem',
                        fontSize: '0.75rem',
                        borderRadius: 4,
                        border: '1px solid var(--header-border)',
                        background: 'var(--widget-bg)',
                        color: 'var(--text)',
                        cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer',
                        opacity: page >= totalPages - 1 ? 0.6 : 1,
                      }}
                    >
                      Next
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
